// src/hooks/useAuth.js — Supabase session tracking for the scheduler login gate.
import { useState, useEffect } from 'react'
import { supabase } from '../db/index.js'
import { clearSensitiveStorage, deleteSensitiveDatabases } from '../lib/signOutCleanup.js'
import { clearMemoryCache } from '../db/queryCache.js'

// session: undefined = still loading, null = logged out, object = logged in.
// In local mode (no Supabase configured) there is no auth — callers treat null
// as "no gate" via authEnabled.
export const authEnabled = !!supabase

export function useAuth() {
  const [session, setSession] = useState(authEnabled ? undefined : null)

  useEffect(() => {
    if (!authEnabled) return
    supabase.auth.getSession().then(({ data }) => setSession(data.session ?? null))
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s ?? null))
    return () => sub.subscription.unsubscribe()
  }, [])

  return session
}

export function userDisplayName(session) {
  if (!session?.user) return null
  return session.user.user_metadata?.name || (session.user.email || '').split('@')[0] || 'Admin'
}

export async function signOut() {
  // Forget what this browser holds about clients BEFORE the session ends (lib/signOutCleanup.js):
  // contract autosaves carry party ID numbers, and the next person at this computer is not the
  // agent. Wrapped so that a cleanup failure can never keep someone signed in.
  try {
    clearSensitiveStorage(typeof localStorage !== 'undefined' ? localStorage : null)
    clearMemoryCache()
    await deleteSensitiveDatabases(typeof indexedDB !== 'undefined' ? indexedDB : null)
  } catch { /* sign out regardless */ }
  // ⚠️ scope 'local' on purpose (owner 2026-10-13): end THIS browser's session only. supabase-js
  // defaults to 'global', which revokes every session of the account — so Sign out on an office PC
  // also logged the agent's phone out and killed the session the browser extension holds for the
  // same account. Reverting to the bare call brings back "I keep getting logged out".
  if (authEnabled) await supabase.auth.signOut({ scope: 'local' })
}

// Change the current user's password — no old password needed (uses the session).
export async function changePassword(newPassword) {
  const { error } = await supabase.auth.updateUser({ password: newPassword })
  if (error) throw error
}
